import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Edit2 } from "lucide-react";

const ICON_OPTS = ["Wrench", "Settings", "Lightbulb", "Phone", "Shield", "Zap", "Camera", "Lock"];
const empty = () => ({ name: "", icon: "Wrench", description: "", features: [], order: 0, active: true });

export default function AdminServicesEdit() {
    const [items, setItems] = React.useState([]);
    const [editing, setEditing] = React.useState(null);

    const load = React.useCallback(() => api.get("/admin/services").then(r => setItems(r.data || [])), []);
    React.useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            if (editing.service_id) await api.put(`/admin/services/${editing.service_id}`, editing);
            else await api.post("/admin/services", editing);
            toast.success("Salvat"); setEditing(null); load();
        } catch { toast.error("Eroare"); }
    };
    const del = async (id) => {
        if (!window.confirm("Ștergi serviciul?")) return;
        await api.delete(`/admin/services/${id}`); load();
    };
    const updateFeature = (i, v) => setEditing(e => ({ ...e, features: e.features.map((f, idx) => idx === i ? v : f) }));
    const addFeature = () => setEditing(e => ({ ...e, features: [...(e.features || []), ""] }));
    const removeFeature = (i) => setEditing(e => ({ ...e, features: e.features.filter((_, idx) => idx !== i) }));

    return (
        <div data-testid="admin-services">
            <div className="flex items-start justify-between mb-6">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl">Catalog servicii</h1></div>
                <button onClick={() => setEditing(empty())} className="btn-primary flex items-center gap-2" data-testid="svc-create"><Plus className="h-4 w-4" /> Serviciu nou</button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Editează serviciile afișate public pe pagina /servicii. Dacă tabela e goală, se folosesc 4 servicii default (doar pe public).</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.length === 0 && <p className="text-muted-foreground col-span-full py-8 text-center">Niciun serviciu personalizat salvat.</p>}
                {items.map(s => (
                    <div key={s.service_id} className="card-soft p-4" data-testid={`svc-row-${s.service_id}`}>
                        <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="font-medium">{s.name} {!s.active && <span className="text-xs text-muted-foreground">(inactiv)</span>}</div>
                                <div className="text-xs text-muted-foreground">{s.icon} · ordine: {s.order}</div>
                                <p className="text-sm mt-1 text-muted-foreground line-clamp-2">{s.description}</p>
                                <div className="text-xs mt-1">{(s.features || []).length} features</div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => setEditing(s)} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                <button onClick={() => del(s.service_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <h3 className="font-heading text-xl">{editing.service_id ? "Editează serviciu" : "Serviciu nou"}</h3>
                            <button onClick={() => setEditing(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nume serviciu" className="input-soft" data-testid="svc-edit-name" />
                            <select value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className="input-soft">
                                {ICON_OPTS.map(i => <option key={i}>{i}</option>)}
                            </select>
                            <textarea rows="3" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Descriere" className="input-soft" />
                            <div>
                                <label className="text-xs text-muted-foreground block mb-1">Caracteristici (afișate ca bullet)</label>
                                <div className="space-y-2">
                                    {(editing.features || []).map((f, i) => (
                                        <div key={i} className="flex gap-2">
                                            <input value={f} onChange={(e) => updateFeature(i, e.target.value)} className="input-soft flex-1" />
                                            <button onClick={() => removeFeature(i)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" onClick={addFeature} className="btn-outline !py-1 text-sm mt-2 flex items-center gap-1"><Plus className="h-3 w-3" /> Adaugă feature</button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} placeholder="Ordine" className="input-soft" />
                                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />Activ</label>
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={save} className="btn-primary" data-testid="svc-edit-save">Salvează</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
